/**
 * Metrics API Query Formatter
 *
 * Formats Dashboard queries into Metrics API compatible format.
 * Includes metadata for transforming API responses back to the format expected by charts.
 */

import {
  AttemptFilters,
  EventFilters,
  EventsByIssueFilters,
  QueueDepthFilters,
  Granularity as MetricsGranularity,
  RequestFilters,
  TransformationFilters,
} from '../../../../../typings/Metrics.interface';
import { EventListFiltersProps } from '../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../typings/RequestList.interface';

// Shared types previously from formatQuery
export type Granularity = 'month' | 'day' | 'hour' | 'minute' | 'second';

export type MetricModel =
  | 'EventRequestsTransformations'
  | 'TransformationExecutions'
  | 'Events'
  | 'EventsRate'
  | 'Requests'
  | 'IgnoredEvents'
  | 'Attempts'
  | 'EventsWithIssues'
  | 'QueueDepth'
  | 'EventsPendingTimeseries';

export interface QueryParams {
  filters?: EventListFiltersProps | RequestListFiltersProps | any;
  granularity?: Granularity;
  dimensions?: string[];
  time_dimention?: string;
  measure?: string | string[];
}

// Map granularity to Metrics API granularity unit letter
const GRANULARITY_UNIT: Record<Granularity, string> = {
  second: 's',
  minute: 'm',
  hour: 'h',
  day: 'd',
  month: 'M',
};

export function buildApiGranularity(granularity: Granularity, unit = 1): MetricsGranularity {
  return `${unit}${GRANULARITY_UNIT[granularity]}`;
}

// Metrics API endpoints
export type MetricsEndpoint =
  | 'requests'
  | 'events'
  | 'attempts'
  | 'events-by-issue'
  | 'transformations'
  | 'queue-depth'
  | 'events-pending-timeseries';

const MODEL_TO_ENDPOINT: Record<MetricModel, MetricsEndpoint | null> = {
  Requests: 'requests',
  Events: 'events',
  Attempts: 'attempts',
  EventsWithIssues: 'events-by-issue',
  EventRequestsTransformations: 'transformations',
  TransformationExecutions: 'transformations',
  QueueDepth: 'queue-depth',
  EventsPendingTimeseries: 'events-pending-timeseries',
  IgnoredEvents: null, // Not yet migrated
  EventsRate: null, // Not yet migrated
};

// Note: Measure names are now expected to be in snake_case format directly from metric definitions
// No conversion needed - metric definitions now use snake_case (e.g., 'accepted_count', 'avg_events_per_request')

// Map dimension names to Metrics API dimension names
// Note: Now using snake_case consistently, so most mappings are 1:1
const DIMENSION_MAP: Record<string, Record<string, string>> = {
  Requests: {
    source_id: 'source_id',
    rejection_cause: 'rejection_cause',
    status: 'status',
  },
  Events: {
    source_id: 'source_id',
    webhook_id: 'webhook_id',
    destination_id: 'destination_id',
    delivery_group: 'delivery_group',
    status: 'status',
  },
  Attempts: {
    destination_id: 'destination_id',
    delivery_group: 'delivery_group',
    status: 'status',
  },
  QueueDepth: {
    destination_id: 'destination_id',
    delivery_group: 'delivery_group',
  },
  EventsWithIssues: {
    issue_id: 'issue_id',
  },
  EventRequestsTransformations: {
    transformation_id: 'transformation_id',
    webhook_id: 'webhook_id',
    log_level: 'log_level',
    issue_id: 'issue_id',
  },
  TransformationExecutions: {
    transformation_id: 'transformation_id',
    webhook_id: 'webhook_id',
    log_level: 'log_level',
    issue_id: 'issue_id',
  },
  EventsPendingTimeseries: {
    destination_id: 'destination_id',
  },
};

export interface MetricsQueryParams {
  endpoint: MetricsEndpoint;
  query_params: {
    date_range: {
      start: string;
      end: string;
    };
    granularity?: MetricsGranularity;
    measures: string[];
    dimensions?: string[];
    filters?:
      | RequestFilters
      | EventFilters
      | AttemptFilters
      | EventsByIssueFilters
      | QueueDepthFilters
      | TransformationFilters;
  };
  // Metadata for response transformation
  model: MetricModel;
  measure: string;
  time_dimension: string;
  granularity?: Granularity;
}

/**
 * Convert Request filters to Metrics API format
 */
function convertRequestFilters(filters: RequestListFiltersProps): RequestFilters {
  const metrics_filters: RequestFilters = {};

  // Pass source_id filter to API (supports both single value and arrays)
  if (filters.source_id) {
    metrics_filters.source_id = filters.source_id;
  }

  if (filters.status === 'accepted') {
    metrics_filters.status = 'ACCEPTED';
  } else if (filters.status === 'rejected') {
    metrics_filters.status = 'REJECTED';
  }

  if (filters.rejection_cause && filters.rejection_cause.length > 0) {
    // Pass rejection_cause array to API (supports both single value and arrays)
    if (filters.rejection_cause.length === 1) {
      metrics_filters.rejection_cause = filters.rejection_cause[0];
    } else {
      metrics_filters.rejection_cause = filters.rejection_cause;
    }
  }

  if (filters.bulk_retry_id) {
    metrics_filters.bulk_retry_ids = Array.isArray(filters.bulk_retry_id)
      ? filters.bulk_retry_id
      : [filters.bulk_retry_id];
  }

  if (filters.events_count?.min || filters.events_count?.max) {
    metrics_filters.events_count = {
      ...(filters.events_count.min ? { min: parseInt(filters.events_count.min, 10) } : {}),
      ...(filters.events_count.max ? { max: parseInt(filters.events_count.max, 10) } : {}),
    };
  }

  if (filters.ignored_count?.min || filters.ignored_count?.max) {
    metrics_filters.ignored_count = {
      ...(filters.ignored_count.min ? { min: parseInt(filters.ignored_count.min, 10) } : {}),
      ...(filters.ignored_count.max ? { max: parseInt(filters.ignored_count.max, 10) } : {}),
    };
  }

  return metrics_filters;
}

/**
 * Convert Event filters to Metrics API format
 */
function convertEventFilters(filters: EventListFiltersProps): EventFilters {
  const metrics_filters: EventFilters = {};
  const event_filters = filters as EventListFiltersProps & {
    delivery_group?: string | (string | null)[] | null;
  };

  // Pass filters to API (supports both single values and arrays)
  if (filters.source_id) {
    metrics_filters.source_id = filters.source_id;
  }

  if (filters.webhook_id) {
    metrics_filters.webhook_id = filters.webhook_id;
  }

  if (filters.destination_id) {
    metrics_filters.destination_id = filters.destination_id;
  }

  if (event_filters.delivery_group !== undefined) {
    metrics_filters.delivery_group = event_filters.delivery_group;
  }

  if (filters.status && filters.status.length > 0) {
    // Map status from lowercase UI values to uppercase API enum values
    // UI uses: 'failed', 'pending', 'paused', 'scheduled', 'successful', etc.
    // API expects: 'FAILED', 'QUEUED', 'HOLD', 'SCHEDULED', 'SUCCESSFUL', etc.
    const status_mapping: Record<string, string> = {
      // Lowercase: UI filter components and URL query parameters
      successful: 'SUCCESSFUL',
      failed: 'FAILED',
      pending: 'QUEUED', // UI term for QUEUED status
      queued: 'QUEUED',
      paused: 'HOLD', // UI term for HOLD status
      scheduled: 'SCHEDULED',
      cancelled: 'CANCELLED',
      // Uppercase: Direct API enum values (pass-through)
      SUCCESSFUL: 'SUCCESSFUL',
      FAILED: 'FAILED',
      QUEUED: 'QUEUED',
      HOLD: 'HOLD',
      SCHEDULED: 'SCHEDULED',
      CANCELLED: 'CANCELLED',
    };

    // Map all status values (support single value or array)
    const mapped_statuses = filters.status
      .map((status: string) => status_mapping[status])
      .filter(Boolean);

    if (mapped_statuses.length === 1) {
      // Single status - pass as string for backward compatibility
      metrics_filters.status = mapped_statuses[0] as EventFilters['status'];
    } else if (mapped_statuses.length > 1) {
      // Multiple statuses - pass as array
      metrics_filters.status = mapped_statuses as (
        | 'SUCCESSFUL'
        | 'FAILED'
        | 'QUEUED'
        | 'CANCELLED'
        | 'HOLD'
        | 'SCHEDULED'
      )[];
    }
  }

  if (filters.error_code && filters.error_code.length > 0) {
    metrics_filters.error_code = filters.error_code[0];
  }

  if (filters.issue_id) {
    metrics_filters.issue_ids = Array.isArray(filters.issue_id)
      ? filters.issue_id
      : [filters.issue_id];
  }

  if (filters.bulk_retry_id) {
    metrics_filters.bulk_retry_ids = Array.isArray(filters.bulk_retry_id)
      ? filters.bulk_retry_id
      : [filters.bulk_retry_id];
  }

  if (filters.cli_id) {
    const cli_id_value = Array.isArray(filters.cli_id) ? filters.cli_id[0] : filters.cli_id;
    // Only set if it's a string (not APIAnyOperator)
    if (typeof cli_id_value === 'string') {
      metrics_filters.cli_id = cli_id_value;
    }
  }

  if (filters.cli_user_id && Array.isArray(filters.cli_user_id)) {
    metrics_filters.cli_user_id = filters.cli_user_id[0];
  }

  if (filters.attempts?.min) {
    metrics_filters.attempts = parseInt(filters.attempts.min, 10);
  }

  if (filters.response_status) {
    if (Array.isArray(filters.response_status)) {
      metrics_filters.response_status = parseInt(filters.response_status[0], 10);
    } else if (filters.response_status.min) {
      metrics_filters.response_status = parseInt(filters.response_status.min, 10);
    }
  }

  return metrics_filters;
}

/**
 * Convert Attempt filters to Metrics API format
 */
function convertAttemptFilters(
  filters: EventListFiltersProps | RequestListFiltersProps | any,
): AttemptFilters {
  const metrics_filters: AttemptFilters = {};

  // Pass destination_id filter to API (supports both single value and arrays)
  if (filters.destination_id) {
    metrics_filters.destination_id = filters.destination_id;
  }

  if (filters.delivery_group !== undefined) {
    metrics_filters.delivery_group = filters.delivery_group;
  }

  if (filters.status && filters.status.length > 0) {
    // Support both single status and multiple statuses
    if (filters.status.length === 1) {
      metrics_filters.status = filters.status[0];
    } else {
      metrics_filters.status = filters.status;
    }
  }

  return metrics_filters;
}

/**
 * Convert QueueDepth filters to Metrics API format
 */
function convertQueueDepthFilters(
  filters: EventListFiltersProps | RequestListFiltersProps | any,
): QueueDepthFilters {
  const metrics_filters: QueueDepthFilters = {};

  if (filters.destination_id) {
    metrics_filters.destination_id = filters.destination_id;
  }

  if (filters.delivery_group !== undefined) {
    metrics_filters.delivery_group = filters.delivery_group;
  }

  return metrics_filters;
}

/**
 * Convert EventsByIssue filters to Metrics API format
 */
function convertEventsByIssueFilters(
  filters: EventListFiltersProps | RequestListFiltersProps | any,
): Partial<EventsByIssueFilters> {
  const metrics_filters: Partial<EventsByIssueFilters> = {};

  if (filters.issue_id) {
    metrics_filters.issue_id = filters.issue_id;
  }

  if (filters.source_id) {
    metrics_filters.source_id = filters.source_id;
  }

  if (filters.destination_id) {
    metrics_filters.destination_id = filters.destination_id;
  }

  if (filters.webhook_id) {
    metrics_filters.webhook_id = filters.webhook_id;
  }

  return metrics_filters;
}

/**
 * Convert Transformation filters to Metrics API format
 */
function convertTransformationFilters(
  filters: EventListFiltersProps | RequestListFiltersProps | any,
): TransformationFilters {
  const metrics_filters: TransformationFilters = {};

  // Pass filters to API (supports both single values and arrays)
  if (filters.transformation_id) {
    metrics_filters.transformation_id = filters.transformation_id;
  }

  if (filters.webhook_id) {
    metrics_filters.webhook_id = filters.webhook_id;
  }

  if (filters.log_level) {
    metrics_filters.log_level = filters.log_level;
  }

  if (filters.issue_id) {
    if (!Array.isArray(filters.issue_id)) {
      metrics_filters.issue_id = filters.issue_id;
    } else if (filters.issue_id.length === 1) {
      metrics_filters.issue_id = filters.issue_id[0];
    }
  }

  return metrics_filters;
}

/**
 * Main function to convert metric query to Metrics API query
 */
export function formatMetricsQuery(
  model: MetricModel,
  params: QueryParams & { limit?: number },
): MetricsQueryParams | null {
  const {
    filters = {},
    measure = 'count',
    granularity,
    dimensions = [],
    time_dimention = 'createdAt',
  } = params;

  // Check if this model has a metrics API endpoint
  const endpoint = MODEL_TO_ENDPOINT[model];
  if (!endpoint) {
    // Model not yet migrated to Metrics API
    return null;
  }

  // Build date range
  if (!filters.date || (!filters.date.min && !filters.date.max)) {
    // No date range provided
    return null;
  }

  const date_range = {
    start: filters.date.min || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    end: filters.date.max || new Date().toISOString(),
  };

  // Convert granularity
  const metrics_granularity = granularity ? buildApiGranularity(granularity) : null;

  // Measure names are already in snake_case from metric definitions
  const metrics_measures = Array.isArray(measure) ? measure : [measure];

  // Convert dimensions
  const dimension_mapping = DIMENSION_MAP[model] || {};
  const metrics_dimensions = dimensions.map((dim) => dimension_mapping[dim] || dim).filter(Boolean);

  // Convert filters based on model type
  let metrics_filters:
    | RequestFilters
    | EventFilters
    | AttemptFilters
    | Partial<EventsByIssueFilters>
    | QueueDepthFilters
    | TransformationFilters = {};
  switch (model) {
    case 'Requests':
      metrics_filters = convertRequestFilters(filters);
      break;
    case 'Events':
    case 'EventsPendingTimeseries':
      metrics_filters = convertEventFilters(filters);
      break;
    case 'Attempts':
      metrics_filters = convertAttemptFilters(filters);
      break;
    case 'QueueDepth':
      metrics_filters = convertQueueDepthFilters(filters);
      break;
    case 'EventsWithIssues':
      metrics_filters = convertEventsByIssueFilters(filters);
      break;
    case 'EventRequestsTransformations':
    case 'TransformationExecutions':
      metrics_filters = convertTransformationFilters(filters);
      break;
  }

  const result = {
    endpoint,
    query_params: {
      date_range,
      granularity: metrics_granularity,
      measures: metrics_measures,
      dimensions: metrics_dimensions.length > 0 ? metrics_dimensions : undefined,
      filters: Object.keys(metrics_filters).length > 0 ? metrics_filters : undefined,
    },
    model,
    measure: metrics_measures[0],
    time_dimension: time_dimention,
    granularity,
  };

  return result;
}

export default formatMetricsQuery;
